const CHUNK_UPDATE_SESSION_KEY = 'ESQUICK_CHUNK_UPDATE_RELOAD_COUNT';
const CHUNK_UPDATE_MAX_RELOADS = 2;
let chunkUpdateRecoveryInProgress = false;

const chunkErrorPatterns = [
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'error loading dynamically imported module',
  'Unable to preload CSS',
  'ChunkLoadError',
  'Loading chunk',
  'dynamically imported module',
];

const getErrorText = (error: unknown) => {
  if (error instanceof Error) {
    return `${error.name} ${error.message}`;
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error && typeof error === 'object') {
    const maybeMessage = 'message' in error ? String(error.message) : '';
    const maybeReason = 'reason' in error ? String(error.reason) : '';
    return `${maybeMessage} ${maybeReason}`;
  }

  return '';
};

export const isChunkLoadError = (error: unknown) => {
  const errorText = getErrorText(error);
  return chunkErrorPatterns.some(pattern => errorText.includes(pattern));
};

const clearBrowserCaches = async () => {
  if (!('caches' in window)) {
    return;
  }

  const cacheNames = await caches.keys();
  await Promise.all(cacheNames.map(cacheName => caches.delete(cacheName)));
};

const updateServiceWorkers = async () => {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(
    registrations.map(async registration => {
      await registration.update().catch(() => undefined);

      if (registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
    }),
  );
};

const buildReloadUrl = () => {
  const url = new URL(window.location.href);
  url.searchParams.set('__app_update', String(Date.now()));
  return url.toString();
};

export const recoverFromChunkLoadError = async () => {
  if (chunkUpdateRecoveryInProgress) {
    return;
  }

  chunkUpdateRecoveryInProgress = true;
  const reloadCount = Number(sessionStorage.getItem(CHUNK_UPDATE_SESSION_KEY) ?? '0');

  if (reloadCount >= CHUNK_UPDATE_MAX_RELOADS) {
    sessionStorage.removeItem(CHUNK_UPDATE_SESSION_KEY);
    chunkUpdateRecoveryInProgress = false;
    return;
  }

  sessionStorage.setItem(CHUNK_UPDATE_SESSION_KEY, String(reloadCount + 1));

  try {
    await updateServiceWorkers();
    await clearBrowserCaches();
  } finally {
    window.location.replace(buildReloadUrl());
  }
};

export const clearChunkUpdateReloadState = () => {
  sessionStorage.removeItem(CHUNK_UPDATE_SESSION_KEY);
};
